import { AxiosError, AxiosResponse } from "axios";
import {
  Button,
  Container,
  Form,
  Link,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { Error } from "components/Error/Error";
import { FormActions } from "components/FormActions/FormActions";
import { Otp } from "components/Otp/Otp";
import { EnhancedAccount } from "library/accounts/types";
import { putHref } from "library/api";
import {
  ApiError,
  ErrorType,
  UpdateAddressResponse,
} from "library/api/address/types.generated";
import { yesNoOptions } from "library/sharedOptions";
import { useReducer } from "react";
import { useMutation } from "react-query";
import { AccountHeader } from "../../components/AccountHeader";
import { Address } from "../../components/Address";
import { useNav } from "../../hooks/useNav";
import { reasonOptions } from "../../options";
import { FullPersonalDetails, ResidentialFormValues } from "../../types";
import {
  getCreateSessionUrl,
  residentialFormValuesToAddress,
} from "../../utils";
import { ResidentialAddressSuccess } from "../ResidentialAddressSuccess/ResidentialAddressSuccess";
import { initialState } from "./initialState";
import { ManualAddress } from "./ManualAddress";
import { reducer } from "./reducer";
import { ActionTypes } from "./types";

const isTest = process.env.NODE_ENV === "test";

interface Props {
  account: EnhancedAccount;
  fullPersonalDetails: FullPersonalDetails;
}
export const AddressForm = ({ account, fullPersonalDetails }: Props) => {
  const { goBack, navigate } = useNav();

  const [state, dispatch] = useReducer(reducer, initialState);

  const { Field, methods } = useBasisForm<ResidentialFormValues>();
  const { getValues } = methods;
  const token = account.credit_card?.token;

  const updateMutation = useMutation<
    AxiosResponse<UpdateAddressResponse>,
    AxiosError<ApiError>,
    { values: ResidentialFormValues; token2FaRenewed?: string }
  >(({ values, token2FaRenewed }) =>
    putHref(
      fullPersonalDetails.updateEndpoint,
      {
        address: residentialFormValuesToAddress(values.address),
        reason_for_change: values.reason,
        mailing_same_as_residential: values.mailingAddressIsTheSame === "yes",
      },
      isTest
        ? {
            "fake-session-expiry": token2FaRenewed!, // header values are always strings
          }
        : {},
    ),
  );

  const handleSubmitSuccess = (
    { data }: AxiosResponse<UpdateAddressResponse>,
    values,
  ) => {
    dispatch({
      type: ActionTypes.SUBMIT_SUCCESS,
      payload: { apiData: data, formValues: values },
    });
  };

  const handleError = (
    values: ResidentialFormValues,
    error: AxiosError<ApiError>,
  ) => {
    const errorType = error.response?.data?.type ?? null;
    const createSessionUrl = getCreateSessionUrl({
      links: error.response?.data.links,
    });

    if (errorType === ErrorType.REQUIRED_2FA && createSessionUrl !== "") {
      dispatch({
        type: ActionTypes.SUBMIT_2FA_REQUIRED,
        payload: { createSessionUrl, token },
      });
    } else if (error.response?.data.payload) {
      dispatch({
        type: ActionTypes.SUBMIT_ISSUES,
        payload: {
          apiError: error.response?.data,
          formValues: values,
        },
      });
    } else {
      dispatch({
        type: ActionTypes.SUBMIT_ERROR,
        payload: { apiError: error.response?.data },
      });
    }
  };

  const onSubmit = (values: ResidentialFormValues) => {
    updateMutation.mutate(
      { values },
      {
        onError: (error) => handleError(values, error),
        onSuccess: (response) => handleSubmitSuccess(response, values),
      },
    );
  };

  switch (state.status) {
    case "2FA_REQUIRED": {
      return (
        <Otp
          createSessionUrl={state.createSessionUrl}
          token={state.token}
          errorFooter={
            <Link
              appearance="primary-button"
              width="256"
              href="/manage-personal-details"
              newTab={false}
            >
              Manage personal details
            </Link>
          }
          onSuccess={() => {
            const values = getValues();
            updateMutation.mutate(
              { values, token2FaRenewed: "yes" },
              {
                onError: (error) => handleError(values, error),
                onSuccess: handleSubmitSuccess,
              },
            );
          }}
        />
      );
    }

    case "SUCCESS": {
      return (
        <Stack gap="8">
          <AccountHeader account={account} />
          <ResidentialAddressSuccess apiData={state.payload} />
        </Stack>
      );
    }
    case "ERROR": {
      return <Error />;
    }

    default: {
      return (
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="10">
            <AccountHeader account={account} />
            <Stack gap="4">
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Current residential address
              </Text>
              {fullPersonalDetails.residential && (
                <Address address={fullPersonalDetails.residential} />
              )}
            </Stack>
            <Stack gap="4">
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                New residential address
              </Text>
              <Stack gap="8">
                <ManualAddress region={account.region} />
                <Field
                  as={RadioGroup}
                  label="Reason for updating your address"
                  name="reason"
                  options={reasonOptions}
                />
                <Container width="100%" width-xs="440">
                  <Field
                    as={RadioGroup}
                    label="Is your mailing address the same as residential address?"
                    name="mailingAddressIsTheSame"
                    options={yesNoOptions}
                  />
                </Container>
                <Container width="100%" width-sm="400">
                  <FormActions>
                    <FormActions.Primary>
                      <Button type="submit" loading={updateMutation.isLoading}>
                        Update
                      </Button>
                    </FormActions.Primary>
                    <FormActions.Secondary>
                      <Button
                        variant="secondary"
                        disabled={updateMutation.isLoading}
                        onClick={goBack}
                      >
                        Back
                      </Button>
                    </FormActions.Secondary>
                  </FormActions>
                </Container>
                <Text>
                  {`Are you wanting to update your `}
                  <Link
                    href="#"
                    newTab={false}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/mailing-address", {
                        state: { account, fullPersonalDetails },
                      });
                    }}
                  >
                    mailing address
                  </Link>
                  {` instead?`}
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Form>
      );
    }
  }
};
